import { resolveTargetProductionSettings } from './target-production';
import { resolveSlotHardwareFromCanvas } from '../hardware/hardware-selection';
import { parseDatasetKeyFromPath } from '../design/design-data';
import { resolveDesignResolution } from '../design/design-resolution';
import { detectShadingHasRun } from '../shading/shading-status';

const sumMonthlyValues = (values) =>
  Array.isArray(values) && values.length >= 12
    ? values.reduce((total, value) => total + (Number(value) || 0), 0)
    : 0;

const countPanelsFromProduction = (productionPerPanel) =>
  Array.isArray(productionPerPanel)
    ? productionPerPanel.reduce(
        (sum, roof) =>
          sum +
          (Array.isArray(roof?.roofPanels)
            ? roof.roofPanels.filter((value) => Number(value) > 0).length
            : 0),
        0
      )
    : 0;

const countStringingPanels = (stringingData) =>
  Array.isArray(stringingData)
    ? stringingData.reduce(
        (sum, string) => sum + (string?.panels?.length || 0),
        0
      )
    : 0;

const countInverters = (invertersUsed) =>
  Array.isArray(invertersUsed)
    ? invertersUsed.reduce((sum, inverter) => sum + (inverter?.count || 0), 0)
    : null;

export const resolveProductionFromCanvas = ({
  canvasData = {},
  packageData = {},
  utility = null,
  iframeContext = null
} = {}) => {
  let totalAnnualKwh = canvasData.total_annual_kwh || 0;
  const totalMonthlyKwh = canvasData.total_monthly_kwh || Array(12).fill(0);
  let totalClippingKwh = canvasData.total_clipping_kwh || 0;
  const totalMonthlyClippingKwh =
    canvasData.total_monthly_clipping_kwh || Array(12).fill(0);
  const stringingData = canvasData.stringing_data || null;
  const invertersUsed = canvasData.inverters_used || null;
  const productionPerPanelData = canvasData.production_per_panel || null;
  const productionPerStringData = canvasData.production_per_string || null;
  const hourlyProduction = canvasData.hourly_production || null;
  const selectedPanelCount = Array.isArray(stringingData)
    ? countStringingPanels(stringingData)
    : countPanelsFromProduction(productionPerPanelData);
  const productionHandled = Boolean(
    stringingData ||
    invertersUsed ||
    totalAnnualKwh > 0 ||
    totalClippingKwh > 0 ||
    productionPerPanelData ||
    productionPerStringData ||
    hourlyProduction
  );

  if (productionHandled && Number(canvasData.total_annual_kwh || 0) === 0) {
    const derivedAnnualProduction = sumMonthlyValues(totalMonthlyKwh);
    if (derivedAnnualProduction > 0) totalAnnualKwh = derivedAnnualProduction;
  }

  if (productionHandled && Number(canvasData.total_clipping_kwh || 0) === 0) {
    const derivedClipping = sumMonthlyValues(totalMonthlyClippingKwh);
    if (derivedClipping > 0) totalClippingKwh = derivedClipping;
  }

  const inverterCount = countInverters(invertersUsed);
  const mpptPerInverter =
    packageData?.inverter?.mppt_count_per_inverter !== undefined &&
    packageData?.inverter?.mppt_count_per_inverter !== null
      ? Number(packageData.inverter.mppt_count_per_inverter)
      : null;
  const inverterType = packageData?.inverter?.type;
  const mpptCountTotal =
    canvasData.mppt_count_total !== undefined &&
    canvasData.mppt_count_total !== null
      ? canvasData.mppt_count_total
      : inverterType === 'M' || inverterType === 'Micro'
        ? inverterCount
        : inverterCount !== null && mpptPerInverter
          ? inverterCount * mpptPerInverter
          : null;
  const systemSizeKw =
    canvasData.system_size_kw !== undefined &&
    canvasData.system_size_kw !== null
      ? canvasData.system_size_kw
      : packageData?.panel?.power && selectedPanelCount > 0
        ? (Number(packageData.panel.power) * selectedPanelCount) / 1000
        : null;
  const targetProduction = resolveTargetProductionSettings({
    useTargetProduction: canvasData.use_target_production,
    targetProductionKwh: canvasData.target_production_kwh,
    utility,
    iframeContext
  });

  return {
    totalAnnualKwh,
    totalMonthlyKwh,
    totalClippingKwh,
    totalMonthlyClippingKwh,
    dcAcRatio: canvasData.dc_ac_ratio ?? null,
    stringingData,
    invertersUsed,
    productionPerPanelData,
    productionPerStringData,
    hourlyProduction,
    selectedPanelCount,
    productionHandled,
    productionPerPanel:
      selectedPanelCount > 0 ? totalAnnualKwh / selectedPanelCount : 0,
    systemSizeKw,
    mpptCountTotal,
    ...targetProduction
  };
};

export const hydrateDesignerStateFromCanvas = ({
  state = {},
  canvasData = {},
  utility = null,
  iframeContext = null,
  propertyResolution = null
} = {}) => {
  const currentPackage = state.package || {};
  const panelResolved = resolveSlotHardwareFromCanvas({
    spec: canvasData.selected_panel_spec,
    fkObject: canvasData.selected_panel,
    idValue: canvasData.selected_panel_id,
    catalog: state.solarPanels || []
  });
  const inverterResolved = resolveSlotHardwareFromCanvas({
    spec: canvasData.selected_inverter_spec,
    fkObject: canvasData.selected_inverter,
    idValue: canvasData.selected_inverter_id,
    catalog: state.inverters || []
  });
  const resolvedPanel = panelResolved.hardware || currentPackage.panel || null;
  const resolvedInverter =
    inverterResolved.hardware || currentPackage.inverter || null;
  const packageData = {
    ...currentPackage,
    panel: resolvedPanel || currentPackage.panel,
    inverter: resolvedInverter || currentPackage.inverter,
    selectedPanelSpec:
      panelResolved.source === 'spec'
        ? panelResolved.hardware
        : panelResolved.source === 'none'
          ? currentPackage.selectedPanelSpec || null
          : null,
    selectedInverterSpec:
      inverterResolved.source === 'spec'
        ? inverterResolved.hardware
        : inverterResolved.source === 'none'
          ? currentPackage.selectedInverterSpec || null
          : null,
    inverterQuantity:
      canvasData.inverter_quantity !== undefined &&
      canvasData.inverter_quantity !== null
        ? canvasData.inverter_quantity
        : currentPackage.inverterQuantity,
    additional_inverters:
      canvasData.additional_inverters !== undefined
        ? canvasData.additional_inverters
        : currentPackage.additional_inverters
  };
  const production = resolveProductionFromCanvas({
    canvasData,
    packageData,
    utility,
    iframeContext
  });
  const primaryInverterAutoQuantity =
    canvasData.primary_inverter_auto_quantity !== undefined &&
    canvasData.primary_inverter_auto_quantity !== null
      ? Boolean(canvasData.primary_inverter_auto_quantity)
      : state.primaryInverterAutoQuantity;
  const weatherDatasetFetchedPath =
    typeof canvasData.weather_dataset === 'string'
      ? canvasData.weather_dataset
      : null;

  return {
    ...canvasData,
    resolution: resolveDesignResolution(
      canvasData.resolution,
      state.resolution,
      propertyResolution
    ),
    segments: canvasData.segments,
    package: packageData,
    primaryInverterAutoQuantity,
    hasAutoCalculatedInverterQuantity: !primaryInverterAutoQuantity,
    useTargetProduction: production.useTargetProduction,
    targetProductionKwh: production.targetProductionKwh,
    maxDcAcRatio:
      canvasData.max_dc_ac_ratio !== undefined &&
      canvasData.max_dc_ac_ratio !== null
        ? canvasData.max_dc_ac_ratio
        : state.maxDcAcRatio,
    onlySelectedPanels: Boolean(canvasData.only_selected_panels),
    productionHandled: production.productionHandled,
    totalAnnualKwh: production.totalAnnualKwh || 0,
    totalMonthlyKwh: production.totalMonthlyKwh,
    totalClippingKwh: production.totalClippingKwh || 0,
    totalMonthlyClippingKwh: production.totalMonthlyClippingKwh,
    dcAcRatio: production.dcAcRatio,
    stringingData: production.stringingData,
    invertersUsed: production.invertersUsed,
    selectedPanelCount: production.selectedPanelCount,
    productionPerPanel: production.productionPerPanel,
    productionPerPanelData: production.productionPerPanelData,
    productionPerStringData: production.productionPerStringData,
    hourlyProduction: production.hourlyProduction,
    systemSizeKw: production.systemSizeKw,
    mpptCountTotal: production.mpptCountTotal,
    weatherDatasetSelected:
      parseDatasetKeyFromPath(weatherDatasetFetchedPath) ||
      state.nsrdbDatasetsMeta?.default ||
      'nsrdb_goes_v4_2024',
    weatherDatasetFetchedPath,
    stringingPanelIndexMode: canvasData.stringing_panel_index_mode || 'all',
    shadingHasRun: detectShadingHasRun(canvasData),
    shadingNeedsRerun: false,
    shadingRerunNoticeShown: false
  };
};
